import { useCallback, useEffect, useMemo, useState } from "react";
import { useWindowSize } from "../../hooks/useWindowSize";
import API from "../../service/api";
import { useTranslation } from "react-i18next";
import { MDBBtn, MDBIcon, MDBInput } from "mdb-react-ui-kit";
import { CustomSelect } from "../../components/CustomSelect";
import { Alert } from "../../components/Alert";
import { CustomModal } from "../../components/CustomModal";
import { AdminPage } from "./AdminPage";
import { TextInput } from "../../components/core/inputs/TextInput";
import { PasswordInput } from "../../components/core/inputs/PasswordInput";

export const ProfilePage = ({profile, canUpdateCompany, onUserUpdate}) => {
    const { innerWidth } = useWindowSize();
    const { t } = useTranslation();
    const minimized = useMemo(() => innerWidth < 700, [innerWidth]);

    return (
        <AdminPage
            title={t('profile')}
        >
            <div className="d-flex flex-row w-100">
                <div className={"d-flex me-2 w-100 " + (minimized ? "flex-column" : "flex-row")}>
                    <div className={"d-flex flex-column " + (minimized ? "w-100 mb-2" : "col-6 me-2")} style={{maxWidth: 450}}>
                        <AccountCard profile={profile} minimized={minimized} onUpdate={onUserUpdate}/>
                    </div>
                    
                    <div className={"d-flex flex-column " + (minimized ? "w-100" : 'col-6')} style={{maxWidth: 450}}>
                        <CompanyCard profile={profile} canUpdateCompany={canUpdateCompany} onUpdate={onUserUpdate}/>
                    </div>
                </div>
            </div>
        </AdminPage>
    )
}

const AccountCard = ({
    profile,
    onUpdate
}) => {
    const { t } = useTranslation();
    const [updateProfile, setUpdateProfile] = useState(profile);
    const [selectedCompany, setSelectedCompany] = useState(profile.company?.id);
    const [resetPassword, setResetPassword] = useState(false);
    const [logout, setLogout] = useState(false);
    const [loading, setLoading] = useState(false);
    const [changed, setChanged] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    const inheritedCompany = useMemo(() => profile.rightsCompany !== profile.company.name ? profile.rightsCompany : '', [profile]);

    const onChange = useCallback((ev) => {
        setUpdateProfile(prev => ({
            ...prev,
            [ev.nativeEvent.target.name]: ev.nativeEvent.target.value
        }));
        setChanged(true);
        setMessage(''); setError('');
    }, []);

    const onProfileChange = useCallback((key, val) => {
        setUpdateProfile(prev => ({
            ...prev,
            [key]: val
        }));
        setChanged(true);
        setMessage('');
        setError('');
    }, []);

    const onCompanySelect = useCallback(id => {
        setSelectedCompany(id);
        setChanged(true);
        setMessage(null);
        setError(null);
    }, []);

    const submitUser = useCallback(async () => {
        setLoading(true);
        try {
            await API.PATCH('/api/users', {
                name: updateProfile.name,
                phone: updateProfile.phone,
                company: selectedCompany
            });
            setChanged(false);
            setMessage(t('user_updated_success'));
            localStorage.setItem('lastopcomp', selectedCompany);
            onUpdate();
        }catch(err){
            setError(err.message);
        }finally{
            setChanged(false);
            setLoading(false);
        }  
    }, [updateProfile, selectedCompany]);

    return (
        <div className={"card overflow-auto "}>
            <ChangePasswordModal
                open={resetPassword}
                onClose={() => setResetPassword(false)}
            />
            <LogoutModal
                open={logout}
                onClose={() => setLogout(false)}
            />

            <div className="card-body">
                <h5 className="ms-2 mb-3">{t('your_account')}</h5>
                <TextInput label={t('name')} icon="id-card" value={updateProfile.name} onValueChange={val => onProfileChange("name", val)}/>
                <TextInput label={t('email')} icon="envelope" value={updateProfile.mail} onValueChange={val => onProfileChange("mail", val)}/>
                <TextInput label={t('phone')} icon="mobile-alt" value={updateProfile.phone} onValueChange={val => onProfileChange("phone", val)}/>
                <TextInput label={t('invited_by_full')} icon="user-tag" value={updateProfile.parent??'-'} disabled/>
                
                <div className="d-flex mb flex-row align-items-center">
                    <MDBIcon icon="building" fixed className="me-2"/>
                    <div className="w-100">
                        <CustomSelect
                            title={t('company')}
                            items={profile?.companies.map(c => ({
                                val: c.id, title: c.name
                            }))}
                            selected={selectedCompany}
                            onSelect={onCompanySelect}
                        />
                    </div>
                </div>

                { inheritedCompany && <span className="d-block w-100 text-muted text-end fst-italic" style={{fontSize: '85%'}}>{t('inherited_permissions_from')} <span className="text-warning">{inheritedCompany}</span></span> }

                <Alert type="success" message={message}/>
                <Alert type="danger" message={error}/>

                <div className="d-flex flex-column mt-2">
                    <MDBBtn disabled={!changed || loading} onClick={submitUser}>{t('save')}</MDBBtn>
                    <MDBBtn color="link" onClick={() => setResetPassword(true)}>{t('change_password')}</MDBBtn>
                    <MDBBtn color="link" className="text-danger" onClick={() => setLogout(true)}>{t('logout')}</MDBBtn>
                </div>
            </div>
        </div>
    )
}

const CompanyCard = ({
    profile,
    canUpdateCompany,
    onUpdate
}) => {
    const { t } = useTranslation();
    const [company, setCompany] = useState(profile.company);
    const companyCountry = useMemo(() => company.country, [company]);
    const [countries, setCountries] = useState([]);
    const [timezones, setTimezones] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [changed, setChanged] = useState(false);

    const onChangeCompany = useCallback((key, val) => {
        setCompany(prev => ({
            ...prev,
            [key]: val
        }));
        setChanged(true);
    }, []);

    const onCompanySelect = useCallback(country => {
        setCompany(comp => ({...comp, country}));
        setChanged(true);
    }, []);

    const onGMTSelect = useCallback(GMT => {
        setCompany(comp => ({...comp, GMT}));
        setChanged(true);
    }, []);

    const submitCompany = useCallback(async () => {
        setLoading(true);
        try {
            const GMT = timezones?.find(tz => tz.name == company.GMT)?.name || null;
            await API.PATCH('/api/companies', {
                name: company.name,
                CIF: company.CIF,
                country: company.country,
                GMT
            });
            onUpdate();
        }catch(err){
            setError(err.message);
        }finally {
            setChanged(false);
            setLoading(false);
        }
    }, [company, timezones]);

    useEffect(() => {
        API.GET('/api/config/countries')
            .then(c => setCountries(Array.isArray(c) ? c : []))
            .catch(err => console.error(err));
    }, []);

    useEffect(() => {
        setTimezones(null);
        if (!companyCountry) return;
        API.GET(`/api/config/countries/${companyCountry}/timezones`)
            .then(tzs => {
                setTimezones(tzs.map(tz => {
                    const hours = Math.floor(Math.abs(tz.offset)/60);
                    const negative = tz.offset > 0;
                    const minutes = Math.abs(tz.offset) - hours*60;
    
                    return {
                        name: tz.name,
                        offsetString: `${negative?'-':'+'}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
                    }
                }));
            })
            .catch(error => setError(error.message));
    }, [companyCountry]);

    if (!countries) return null;
    return (
        <div className={"card overflow-auto "}>
            <div className="card-body">
                <h5 className="ms-2 mb-3">{t('your_company')}</h5>
                <TextInput label={t('name')} icon="pencil" value={company.name} onValueChange={val => onChangeCompany('name', val)} disabled={!canUpdateCompany}/>
                <TextInput label={t('cif')} icon="briefcase" value={company.CIF} onValueChange={val => onChangeCompany('CIF', val)} disabled={!canUpdateCompany}/>
                <TextInput label={t('parent')} icon="building" value={company.parent?.name ?? '-'} disabled/>
                <div className="d-flex mb-2 flex-row align-items-center">
                    <MDBIcon icon="earth-europe" fixed className="me-2"/>
                    <div className="w-100">
                        <CustomSelect
                            expandWidth={"245px"} title={t('country')} search={true}
                            items={countries.map(c => ({
                                val: c.code,
                                title: `${c.country}`,
                                icon: `flag-icon flag-icon-${c.code?.toLocaleLowerCase()}`
                            }))}
                            selected={company.country}
                            onSelect={onCompanySelect}
                            disabled={!canUpdateCompany}
                        />
                    </div>
                </div>
                <div className="d-flex mb-2 flex-row align-items-center">
                    <MDBIcon icon="clock" fixed className="me-2"/>
                    <div className="w-100">
                        <CustomSelect expandWidth={"245px"} title={t('timezone')}
                            items={(timezones || []).map(tz => ({
                                val: tz.name,
                                title: `${tz.name} (${tz.offsetString})`
                            }))}
                            selected={company.GMT}
                            onSelect={onGMTSelect}
                            disabled={!canUpdateCompany || !timezones}
                        />
                    </div>
                </div>

                <Alert type="danger" message={error}/>

                {canUpdateCompany && 
                    <button className="btn btn-primary" disabled={!changed || loading} onClick={submitCompany}>{t('save')}</button>
                }
            </div>
        </div>
    )
}

const ChangePasswordModal = ({open, onClose}) => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const { t } = useTranslation();

    const submit = useCallback(async () => {
        try {
            await API.PATCH('/api/users', { password });
            onClose();
        }catch(err){
            setError(err.message);
        }
    }, [password]);

    useEffect(() => {
        if (!open) return;
        setPassword('');
        setConfirmPassword('');
        setError('');
    }, [open]);

    return (
        <CustomModal
            open={open}
            onClose={() => onClose()}
            title={t('change_password')}
            footer={<>
                <MDBBtn color="secondary" onClick={onClose}>{t('cancel')}</MDBBtn>
                <MDBBtn color="primary" disabled={password.length == 0 || password !== confirmPassword} onClick={submit}>{t('change')}</MDBBtn>
            </>}
        >
            <PasswordInput label={t('setpassword')} icon="key" value={password} onValueChange={setPassword}/>
            <PasswordInput label={t('confirmpassword')} icon="key" value={confirmPassword} onValueChange={setConfirmPassword}/>
            <Alert type="danger" message={error}/>
        </CustomModal>
    )
}

const LogoutModal = ({open, onClose}) => {
    const { t } = useTranslation();

    const onConfirm = useCallback(() => {
        API.logout()
            .then(() => {})
            .catch(err => alert(err.message));
        onClose();
    }, []);

    return (
        <CustomModal
            open={open}
            title={t('logout')}
            onClose={onClose}
            footer={(
                <>
                    <MDBBtn color="secondary" onClick={onClose}>{t('cancel')}</MDBBtn>
                    <MDBBtn color="danger" onClick={onConfirm}>{t('confirm')}</MDBBtn>
                </>
            )}
        >
            {t('logout_prompt')}
        </CustomModal>
    )
}